import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_ded0e787-ce73-4799-bf5f-3aa93c0f5b06/artifacts/ujrhxkhv_61A82EC6-6494-4F9F-B1A6-04552E7A8224.jpeg";
const WALL_IMG =
  "https://customer-assets.emergentagent.com/job_ded0e787-ce73-4799-bf5f-3aa93c0f5b06/artifacts/e0itwate_81247389-BE6F-4C43-BDB6-AB97BB569FF1.jpeg";

export const Hero = () => {
  const s = useSiteSettings();
  const name = s.band_name || "The Randy Loveless Band";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  // Accent the second-to-last word; lead with the rest. Handle short names
  // without duplicating words.
  let pre = "";
  let accent = "";
  let tail = "";
  if (parts.length === 1) {
    tail = parts[0];
  } else if (parts.length === 2) {
    [accent, tail] = parts;
  } else {
    pre = parts.slice(0, -2).join(" ");
    accent = parts[parts.length - 2];
    tail = parts[parts.length - 1];
  }

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain-overlay bg-[#0a0a0a]"
    >
      {/* Mobile-only soft backdrop (wall photo, very faded) */}
      <div className="lg:hidden absolute inset-0 opacity-20">
        <img src={WALL_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black" />
      </div>

      <div className="relative z-10 grid lg:grid-cols-12 min-h-screen">
        {/* LEFT — text */}
        <div className="lg:col-span-6 flex flex-col justify-center px-6 md:px-12 lg:pl-20 lg:pr-8 py-24 lg:py-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="h-px w-12 bg-[#DC2626]" />
            <span className="font-oswald text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#DC2626]">
              {s.tagline || "Classic Rock · Montana · Since 2022"}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="font-anton uppercase text-white leading-[0.88] text-[14vw] md:text-[11vw] lg:text-[6.8vw]"
            data-testid="hero-title"
          >
            {pre && (
              <>
                {pre}
                <br />
              </>
            )}
            {accent && (
              <>
                <span className="text-[#B91C1C]">{accent}</span>
                <br />
              </>
            )}
            {tail}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-6 max-w-md text-white/75 text-sm md:text-base font-manrope"
          >
            Six decades of rock 'n' roll, four players, one hell of a night.
            Honest classic rock — the songs you grew up on.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a
              href="#shows"
              data-testid="hero-shows-cta"
              className="font-oswald uppercase tracking-[0.2em] text-xs md:text-sm bg-[#B91C1C] hover:bg-[#991B1B] text-white px-6 md:px-8 py-3 md:py-4 brutal-shadow"
            >
              Upcoming Shows
            </a>
            <a
              href="#music"
              data-testid="hero-music-cta"
              className="font-oswald uppercase tracking-[0.2em] text-xs md:text-sm border-2 border-white text-white hover:bg-white hover:text-black px-6 md:px-8 py-3 md:py-4 transition-colors"
            >
              Hear the Music
            </a>
          </motion.div>
        </div>

        {/* RIGHT — full band photo, contained, no overlap with text */}
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="hidden lg:block lg:col-span-6 relative overflow-hidden"
        >
          <img
            src={HERO_IMG}
            alt="The Randy Loveless Band on stage"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "center 40%" }}
          />
          {/* Top fade — keeps nav links legible over bright stage lights */}
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
          {/* Left seam blending into text side */}
          <div className="absolute inset-y-0 left-0 w-40 bg-gradient-to-r from-[#0a0a0a] to-transparent" />
          {/* Bottom fade */}
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 text-white/50 flex flex-col items-center gap-1"
      >
        <span className="font-oswald text-[10px] uppercase tracking-[0.3em]">
          Scroll
        </span>
        <ChevronDown size={18} />
      </motion.div>
    </section>
  );
};
